import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Building2,
  Clock,
  GraduationCap,
  CheckCircle,
  ExternalLink,
  Info,
  AlertTriangle,
  MapPin,
  Sparkles,
  Share2,
  Link2,
  MessageCircle,
} from "lucide-react";
import Seo from "@/components/Seo";
import { toast } from "sonner";

type Program = {
  id: number;
  name: string;
  provider: string;
  sector: string;
  mode: string;
  eligibility: string;
  duration: string;
  outcome: string;
  description: string;
  isFree: boolean;
  isGlobal: boolean;
  officialUrl: string;
  note?: string;
  tags?: string[];
  pricingType?: "free" | "free_with_paid_certificate" | "paid";
  accessType?: string;
  subGroup?: string;
  fallbackUrl?: string;
  fallbackLabel?: string;
  extraAudience?: string;
  enrolmentEnds?: string; // ISO date (YYYY-MM-DD) of enrolment deadline for cycle-bound courses
};

// Cycle-bound courses (e.g. SWAYAM/NPTEL) auto-switch to "Cycle Closed"
// once their enrolment deadline has passed — no manual recheck needed.
const isCycleClosed = (p: Program): boolean => {
  if (!p.enrolmentEnds) return false;
  const end = new Date(`${p.enrolmentEnds}T23:59:59`);
  return !isNaN(end.getTime()) && end.getTime() < Date.now();
};

const getDomain = (url?: string) => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

const ProgramDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/programs.json", { cache: "no-cache" })
      .then((r) => r.json())
      .then((data: Program[]) => {
        if (!active) return;
        const match = Array.isArray(data)
          ? data.find((p) => String(p.id) === String(id))
          : null;
        setProgram(match ?? null);
      })
      .catch(() => setProgram(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  const domain = getDomain(program?.officialUrl);
  const pt =
    program?.pricingType || (program?.isFree ? "free" : "paid");

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/programs/${id ?? ""}`
      : `https://we-help.in/programs/${id ?? ""}`;
  const shareText = program
    ? `${program.name} — free program via we-help.in\n${program.description}`
    : "Check out this free program on we-help.in";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: program?.name || "we-help.in",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // user cancelled or unsupported — fall back to copy
      }
    }
    handleCopyLink();
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `${shareText}\n${shareUrl}`,
  )}`;

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={
          program
            ? `${program.name} — Free Program Details | We-Help.in`
            : "Program Details | We-Help.in"
        }
        description={
          program?.description ||
          "Curated free program details including eligibility, duration, and outcomes."
        }
        path={`/programs/${id ?? ""}`}
      />
      <Header />
      <main className="flex-1 bg-gradient-to-b from-accent/30 to-background">
        <div className="container py-8 md:py-12 max-w-3xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Programs
            </Link>
          </Button>

          {loading && (
            <p className="text-muted-foreground">Loading program…</p>
          )}

          {!loading && !program && (
            <div className="rounded-xl border border-border bg-card p-8 text-center">
              <h1 className="text-2xl font-semibold mb-2">Program not found</h1>
              <p className="text-muted-foreground mb-4">
                This program may have been removed or the link is incorrect.
              </p>
              <Button asChild variant="hero">
                <Link to="/programs">Browse all programs</Link>
              </Button>
            </div>
          )}

          {!loading && program && (
            <article className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <div className="flex items-start gap-4 mb-4">
                <div className="h-14 w-14 shrink-0 rounded-lg bg-muted flex items-center justify-center border border-border" aria-hidden="true">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground font-medium">
                    {program.provider}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
                    {program.name}
                  </h1>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {isCycleClosed(program) && (
                  <Badge className="bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-100">
                    ⏸️ Cycle Closed – Next Session TBA
                  </Badge>
                )}
                {pt === "free" && <Badge variant="free">✅ 100% Free</Badge>}
                {pt === "free_with_paid_certificate" && (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100">
                    ⚠️ Free to Learn • Certificate Paid
                  </Badge>
                )}
                {pt === "paid" && <Badge variant="secondary">💰 Paid</Badge>}
                <Badge
                  variant={
                    program.mode === "Online"
                      ? "online"
                      : program.mode === "Offline"
                      ? "offline"
                      : "secondary"
                  }
                >
                  {program.mode}
                </Badge>
                <Badge variant="sector">{program.sector}</Badge>
                {program.subGroup && (
                  <Badge variant="secondary">{program.subGroup}</Badge>
                )}
                {program.isGlobal && <Badge variant="outline">🌍 Global</Badge>}
              </div>

              <p className="text-base text-muted-foreground mb-6">
                {program.description}
              </p>

              {program.note && (
                <p className="text-sm text-primary font-medium mb-6 flex items-center gap-1">
                  <Info className="h-4 w-4" />
                  {program.note}
                </p>
              )}

              {program.accessType === "audit" && (
                <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>
                    Course content is free, but the certificate may be paid.
                    On {program.provider}, choose <strong>Audit</strong> or
                    apply for <strong>Financial Aid</strong> — do not pay at
                    checkout.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-3">
                  <GraduationCap className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                      Eligibility
                    </div>
                    <div className="text-foreground leading-snug">
                      {program.eligibility}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-3">
                  <Clock className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                      Duration
                    </div>
                    <div className="text-foreground leading-snug">
                      {program.duration}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-md bg-success/10 px-3 py-3">
                  <CheckCircle className="h-5 w-5 mt-0.5 shrink-0 text-success" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-success font-semibold">
                      Outcome
                    </div>
                    <div className="text-foreground leading-snug">
                      {program.outcome}
                    </div>
                  </div>
                </div>
              </div>

              {program.extraAudience && (
                <div className="mb-6 flex items-start gap-2 text-sm text-muted-foreground">
                  <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>
                    Also useful for:{" "}
                    <span className="text-foreground">
                      {program.extraAudience}
                    </span>
                  </span>
                </div>
              )}

              {program.tags && program.tags.length > 0 && (
                <div className="mb-6 flex flex-wrap gap-1.5">
                  {program.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                <Button
                  asChild
                  variant={isCycleClosed(program) ? "outline" : "hero"}
                  size="lg"
                  className="flex-1"
                >
                  <a
                    href={program.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {isCycleClosed(program)
                      ? "View Official Page"
                      : "Apply on Official Site"}
                    <ExternalLink className="h-4 w-4 ml-1" />
                  </a>
                </Button>
                {program.fallbackUrl && (
                  <Button asChild variant="outline" size="lg">
                    <a
                      href={program.fallbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {program.fallbackLabel || "Alternate source"}
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  </Button>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium mr-1">
                  Share this program
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                >
                  <Share2 className="h-4 w-4 mr-1" /> Share
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  <Link2 className="h-4 w-4 mr-1" /> Copy link
                </Button>
                <Button asChild variant="whatsapp" size="sm">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                  </a>
                </Button>
              </div>

              <p className="mt-6 text-xs text-muted-foreground flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                we-help.in is a discovery platform. Always verify program
                details, fees, and deadlines on the official provider's website
                before applying.
              </p>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProgramDetail;